// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useRef, useState } from 'react'
import * as api from '../../api/client'

const STATUS_COLOR: Record<string, string> = {
  verified: 'var(--color-success,#15803d)', pending: 'var(--color-text-mute)',
  verifying: 'var(--color-accent,#d97706)', failed: '#B23A22',
}

/**
 * SaaS-admin: a pharmacy's custom sending domain via the Azure Communication Services relay (the
 * branded/default path). Mail sends from notifications@theirdomain.com through our ACS resource once
 * the domain is verified. The app auto-provisions the ACS domain via ARM (Provision) and generates the
 * DNS records the customer publishes; Check verification flips it to verified. Onboarding can hand a
 * domain here via the ?provisionDomain= query param, which pre-fills the field.
 */
export default function PharmacySenderDomainCard({ pharmacyId }: { pharmacyId: string }) {
  const [sd, setSd] = useState<api.SenderDomain | null>(null)
  const [domain, setDomain] = useState('')
  const [local, setLocal] = useState('notifications')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [testTo, setTestTo] = useState('')
  const rootRef = useRef<HTMLDivElement | null>(null)

  function hydrate(d: api.SenderDomain) {
    setSd(d)
    if (d.configured) { setDomain(d.domain || ''); setLocal(d.from_local_part || 'notifications') }
  }
  useEffect(() => {
    api.getPharmacySenderDomain(pharmacyId).then(d => {
      hydrate(d)
      if (!d.configured) {
        const pre = new URLSearchParams(window.location.search).get('provisionDomain')
        if (pre) {
          setDomain(pre)
          setMsg('Domain carried over from onboarding — click Provision to generate the DNS records.')
          rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    }).catch(() => {})
  }, [pharmacyId])

  async function save(status?: string) {
    if (!domain.trim()) { setMsg('Enter a domain.'); return }
    setBusy(true); setMsg(null)
    try {
      await api.putPharmacySenderDomain(pharmacyId, { domain: domain.trim(), from_local_part: local.trim() || 'notifications', status })
      const d = await api.getPharmacySenderDomain(pharmacyId); hydrate(d)
      setMsg('Saved.')
    } catch (e: any) { setMsg(e?.message || 'Save failed.') }
    finally { setBusy(false) }
  }

  async function provision() {
    setBusy(true); setMsg(null)
    try {
      await api.putPharmacySenderDomain(pharmacyId, { domain: domain.trim(), from_local_part: local.trim() || 'notifications' })
      const r = await api.provisionPharmacySenderDomain(pharmacyId)
      if (!r.ok) { setMsg(r.error || 'Provisioning failed.') }
      else setMsg('Provisioned — add the DNS records below, then Check verification.')
      hydrate(await api.getPharmacySenderDomain(pharmacyId))
    } catch (e: any) { setMsg(e?.message || 'Provisioning failed.') }
    finally { setBusy(false) }
  }

  async function checkVerify() {
    setBusy(true); setMsg(null)
    try {
      const r = await api.verifyPharmacySenderDomain(pharmacyId)
      if (!r.ok) setMsg(r.error || 'Verification check failed.')
      else setMsg(r.status === 'verified' ? 'Verified — now sending from this domain.' : `Status: ${r.status}. DNS may take time to propagate.`)
      hydrate(await api.getPharmacySenderDomain(pharmacyId))
    } catch (e: any) { setMsg(e?.message || 'Check failed.') }
    finally { setBusy(false) }
  }

  async function sendTest() {
    if (!testTo.trim() || !testTo.includes('@')) { setMsg('Enter a valid recipient email.'); return }
    setBusy(true); setMsg(null)
    try {
      const r = await api.testPharmacySenderDomain(pharmacyId, testTo.trim())
      if (r.ok) setMsg(`Test sent via ${r.via?.toUpperCase()} from ${r.from}. Check the inbox (and spam).`)
      else setMsg(r.error || 'Test send failed.')
    } catch (e: any) { setMsg(e?.message || 'Test send failed.') }
    finally { setBusy(false) }
  }

  const card: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, marginTop: 16, background: 'var(--color-surface)' }
  const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--color-text-mute)', display: 'block', marginBottom: 3 }
  const input: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 7, border: '1px solid var(--color-border)', fontSize: 13, fontFamily: 'inherit' }
  const status = sd?.status || 'none'

  function relName(name: string): string {
    const d = sd?.domain || ''
    if (!d || name === d) return '@'
    return name.endsWith('.' + d) ? name.slice(0, -(d.length + 1)) : name
  }

  function buildPowerShell(): string {
    const recs = sd?.dns_records || []
    const dom = sd?.domain || ''
    const cnames = recs.filter(r => r.type === 'CNAME')
    const txt = recs.filter(r => r.type === 'TXT')
    const spf = txt.filter(r => /^v=spf1/i.test(r.value))
    const ownership = txt.filter(r => !/^v=spf1/i.test(r.value))
    const esc = (v: string) => v.replace(/"/g, '`"')
    const lines: string[] = [
      '# Run in an authenticated Azure session: Connect-AzAccount',
      `$ResourceGroup = "<DNS-zone-resource-group>"   # the RG holding the ${dom} Azure DNS zone`,
      `$ZoneName      = "${dom}"`,
      '',
      '# Review the existing apex TXT records BEFORE changing anything:',
      '(Get-AzDnsRecordSet -ResourceGroupName $ResourceGroup -ZoneName $ZoneName -Name "@" -RecordType TXT -ErrorAction SilentlyContinue).Records.Value',
      '',
    ]
    if (txt.length) {
      lines.push(
        '# Apex TXT — ADD to the existing record set (never overwrite: that would wipe your live SPF and other TXT records).',
        '$apex = Get-AzDnsRecordSet -ResourceGroupName $ResourceGroup -ZoneName $ZoneName -Name "@" -RecordType TXT -ErrorAction SilentlyContinue',
        'if (-not $apex) { $apex = New-AzDnsRecordSet -ResourceGroupName $ResourceGroup -ZoneName $ZoneName -Name "@" -RecordType TXT -Ttl 3600 }',
      )
      for (const r of ownership) lines.push(`Add-AzDnsRecordConfig -RecordSet $apex -Value "${esc(r.value)}" | Out-Null`)
      if (spf.length) {
        lines.push(
          '# SPF: a domain may have only ONE v=spf1 record. Add ours only if none exists; otherwise merge',
          '# include:spf.protection.outlook.com into your existing SPF and keep a single trailing -all.',
          'if (-not ($apex.Records.Value | Where-Object { $_ -like "v=spf1*" })) {',
          ...spf.map(r => `  Add-AzDnsRecordConfig -RecordSet $apex -Value "${esc(r.value)}" | Out-Null`),
          '}',
        )
      }
      lines.push('Set-AzDnsRecordSet -RecordSet $apex', '')
    }
    for (const c of cnames) {
      lines.push(`New-AzDnsRecordSet -ResourceGroupName $ResourceGroup -ZoneName $ZoneName -Name "${relName(c.name)}" -RecordType CNAME -Ttl 3600 -Overwrite -DnsRecords (New-AzDnsRecordConfig -Cname "${c.value}")`)
    }
    return lines.join('\n')
  }

  function buildAzCli(): string {
    const recs = sd?.dns_records || []
    const dom = sd?.domain || ''
    const txt = recs.filter(r => r.type === 'TXT')
    const spf = txt.filter(r => /^v=spf1/i.test(r.value))
    const ownership = txt.filter(r => !/^v=spf1/i.test(r.value))
    const esc = (v: string) => v.replace(/"/g, '\\"')
    const lines: string[] = [
      '# az login first',
      'RG="<DNS-zone-resource-group>"   # the RG holding the ' + dom + ' Azure DNS zone',
      `ZONE="${dom}"`,
      '',
      '# Review the existing apex TXT BEFORE changing anything:',
      'az network dns record-set txt show -g "$RG" -z "$ZONE" -n "@" --query "txtRecords[].value" -o tsv 2>/dev/null',
      '',
    ]
    // add-record APPENDS to the @ TXT set (does not overwrite) — safe for the ownership token.
    for (const r of ownership)
      lines.push(`az network dns record-set txt add-record -g "$RG" -z "$ZONE" -n "@" --value "${esc(r.value)}"`)
    if (spf.length) {
      lines.push('# SPF: only ONE v=spf1 record is allowed. If the show above already lists a v=spf1 record,',
        '# do NOT run the next line — instead add include:spf.protection.outlook.com to that existing record.')
      for (const r of spf)
        lines.push(`az network dns record-set txt add-record -g "$RG" -z "$ZONE" -n "@" --value "${esc(r.value)}"`)
    }
    for (const c of recs.filter(r => r.type === 'CNAME'))
      lines.push(`az network dns record-set cname set-record -g "$RG" -z "$ZONE" -n "${relName(c.name)}" --cname "${c.value}"`)
    return lines.join('\n')
  }

  async function copy(text: string, label: string) {
    try { await navigator.clipboard.writeText(text); setMsg(`${label} copied to clipboard.`) }
    catch { setMsg('Copy failed — select the text manually.') }
  }

  return (
    <div ref={rootRef} style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h3 style={{ fontSize: 15, margin: 0 }}>Sending domain (recommended)</h3>
        {sd?.configured && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: STATUS_COLOR[status] || 'var(--color-text-mute)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{status}</span>
        )}
      </div>
      <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 12 }}>
        Sends this pharmacy's mail from their own domain through our Azure relay — no mail server or password to manage. Once verified, this takes priority over SMTP.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div><label style={label}>Domain</label><input style={input} value={domain} onChange={e => setDomain(e.target.value)} placeholder="applespecialtyrx.com" /></div>
        <div><label style={label}>From mailbox</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input style={{ ...input, maxWidth: 130 }} value={local} onChange={e => setLocal(e.target.value.toLowerCase().replace(/[^a-z0-9._+-]/g, ''))} placeholder="notifications" />
            <span style={{ fontSize: 13, color: 'var(--color-text-mute)' }}>@{domain || 'domain'}</span>
          </div>
        </div>
      </div>

      {sd?.dns_records && sd.dns_records.length > 0 && (
        <div style={{ marginBottom: 12, border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ fontSize: 11, color: 'var(--color-text-mute)', padding: '6px 10px', background: 'var(--color-bg-subtle,#f8fafc)' }}>Add these DNS records, then re-check:</div>
          <div style={{ fontSize: 10.5, color: 'var(--color-warning,#b45309)', padding: '4px 10px 6px', background: 'var(--color-bg-subtle,#f8fafc)', lineHeight: 1.4 }}>
            Add to existing records — don't replace the apex TXT set. A domain may have only one <code>v=spf1</code> record: if one already exists, merge <code>include:spf.protection.outlook.com</code> into it rather than adding a second.
          </div>
          {sd.dns_records.map((rec, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '60px 1fr 1.4fr', gap: 8, fontSize: 11.5, fontFamily: 'monospace', padding: '6px 10px', borderTop: '1px solid var(--color-border)' }}>
              <span style={{ fontWeight: 700 }}>{rec.type}</span>
              <span style={{ overflowWrap: 'anywhere' }}>{rec.name}</span>
              <span style={{ overflowWrap: 'anywhere', color: 'var(--color-text-mute)' }}>{rec.value}</span>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, padding: '8px 10px', borderTop: '1px solid var(--color-border)', background: 'var(--color-bg-subtle,#f8fafc)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--color-text-mute)', alignSelf: 'center' }}>If the zone is in Azure DNS, paste into a logged-in session:</span>
            <button onClick={() => copy(buildPowerShell(), 'PowerShell')} style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: 6, padding: '4px 10px', fontSize: 11.5, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--color-text)' }}>Copy PowerShell</button>
            <button onClick={() => copy(buildAzCli(), 'az CLI')} style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: 6, padding: '4px 10px', fontSize: 11.5, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--color-text)' }}>Copy az CLI</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={provision} disabled={busy} title="Create the domain in Azure and generate the DNS records to publish" style={{ background: 'var(--color-primary,#2563eb)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', opacity: busy ? 0.6 : 1 }}>{busy ? 'Working…' : 'Provision (get DNS records)'}</button>
        {sd?.dns_records && sd.dns_records.length > 0 && (
          <button onClick={checkVerify} disabled={busy} style={{ background: 'none', border: '1px solid var(--color-success,#15803d)', color: 'var(--color-success,#15803d)', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Check verification</button>
        )}
        <button onClick={() => save()} disabled={busy} title="Store the domain/sender name without provisioning" style={{ background: 'none', border: '1px solid var(--color-border)', color: 'var(--color-text-mute)', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Save only</button>
        {msg && <span style={{ fontSize: 12.5, color: /fail|error|Status:/i.test(msg) ? '#B23A22' : 'var(--color-success,#15803d)' }}>{msg}</span>}
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-mute)', marginTop: 8 }}>
        Click <b>Provision</b> — the app creates the domain in Azure and lists the DNS records to publish <b>in that domain's DNS zone</b>. After the records are added there, click <b>Check verification</b>. ("Save only" just stores the name without generating records.)
      </div>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--color-border)' }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Send a test email</div>
        <div style={{ fontSize: 11, color: 'var(--color-text-mute)', marginBottom: 8 }}>
          Sends one message through this pharmacy's transport (its verified domain, else the platform default sender, else its SMTP).
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="email" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="recipient@example.com"
            style={{ flex: '1 1 220px', minWidth: 200, padding: '7px 10px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }} />
          <button onClick={sendTest} disabled={busy} style={{ background: 'var(--color-accent,#d97706)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', opacity: busy ? 0.6 : 1 }}>{busy ? 'Sending…' : 'Send test email'}</button>
        </div>
      </div>
    </div>
  )
}
